/**
 * #1731 (F-10, phase 3) — chat-surface glue around the app-singleton PiP
 * voice session. Port of cpv3 `features/chat-brand/useChatVoicePip.ts`.
 *
 * The session itself lives in `pipVoiceStore` (app-level, so a route change
 * never hangs up the call); this hook owns what only a chat surface needs:
 *  - prior-caption hydration on EVERY (re)mount of a session's conversation
 *    (spec §1.4 C-21: no resume-only gating);
 *  - the live transcript → thread-message merge (the thread is the record;
 *    the widget shows only state);
 *  - the same-thread WATERMARK and the end-of-call refetch (spec §10.1: never
 *    double a turn);
 *  - duplex degradation (`trySendText`) for phase 5's composer routing — a
 *    refused publish pauses typed input with the audience's line, never a
 *    silent drop.
 *
 * TRF has one record per thread: a call started from the open thread persists
 * its turns INTO that thread, so `sameThread` is the common case and the
 * watermark branch is the branch that matters. Hydration only fills in when
 * the PiP floats over a different (or blank) thread.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DuplexSendError,
  priorCaptionsToMessages,
  transcriptToMessages,
  type StartOptions,
} from '@core/voice/pip';
import { fetchPriorCaptions, type PriorCaption } from '@core/voice/captionHydration';
import type { Message } from '@core/chat/types';
import { chatConfig } from '@core/config';
import {
  getPipVoiceSession,
  startPipVoice,
  usePipVoiceSnapshot,
  usePipVoiceTranscript,
} from './pipVoiceStore';

export interface ChatVoicePip {
  enabled: boolean;
  pip: ReturnType<typeof usePipVoiceSnapshot>;
  isLive: boolean;
  /** Live (and, over another thread, hydrated) voice turns for the list. */
  voiceMessages: Message[];
  startVoice: (opts?: StartOptions) => void;
  /**
   * Deliver a typed turn to the live session (phase 5 wires the composer).
   * 'delivered' | 'refused' when the voice path owned the message; false when
   * there is no live session and the text path should proceed.
   */
  trySendText: (text: string) => Promise<'delivered' | 'refused' | false>;
  duplexNotice: string | null;
  duplexPaused: boolean;
}

/**
 * @param currentThreadId the thread the surface currently has open
 * @param onCallEnded fired ONCE when a call on THIS thread reaches 'ended',
 *   before the widget dissolves; the surface refetches history there.
 */
export function useChatVoicePip(
  currentThreadId: string | undefined,
  onCallEnded?: () => void | Promise<unknown>,
): ChatVoicePip {
  const enabled = chatConfig().capabilities.voicePip;
  const pip = usePipVoiceSnapshot();
  const transcript = usePipVoiceTranscript();
  const baseTimeRef = useRef<number>(Date.now());
  const [priorCaptions, setPriorCaptions] = useState<PriorCaption[]>([]);
  const [duplexNotice, setDuplexNotice] = useState<string | null>(null);

  const isLive = pip.state === 'listening' || pip.state === 'speaking';

  const startVoice = useCallback((opts?: StartOptions) => {
    baseTimeRef.current = Date.now();
    setPriorCaptions([]);
    setDuplexNotice(null);
    startPipVoice(opts);
  }, []);

  // A call started from the open thread belongs to it; a call the viewer
  // navigated into (drawer row) matches by the record id. Either way the
  // thread fetch carries the persisted turns, so both mean "same thread".
  const sameRoom = !!pip.conversationId && pip.conversationId === currentThreadId;
  const sameThread = sameRoom || (!!pip.threadId && pip.threadId === currentThreadId);

  // Hydrate prior turns whenever the session's conversation is known — fresh
  // start, re-attach and mid-session remount alike — but only when the PiP
  // floats over a DIFFERENT thread: the open thread's own turns arrived
  // through the normal thread fetch, and hydrating them too doubles them.
  useEffect(() => {
    if (!pip.conversationId || sameThread) {
      setPriorCaptions([]);
      return;
    }
    let cancelled = false;
    void fetchPriorCaptions(pip.conversationId).then((captions) => {
      if (!cancelled) setPriorCaptions(captions);
    });
    return () => {
      cancelled = true;
    };
  }, [pip.conversationId, sameThread]);

  // Refetch the thread when a call on it ends — BEFORE the widget dissolves.
  // Dissolve resets the store to 'idle', and `voiceMessages` is [] on idle, so
  // without this the spoken turns vanished from the thread four seconds after
  // hangup. The turns are already persisted (the agent writes per turn); the
  // thread just had not re-read them. Fires once per call.
  const onCallEndedRef = useRef(onCallEnded);
  onCallEndedRef.current = onCallEnded;
  const endedForRef = useRef<string | null>(null);
  // Once the refetch has landed, the fetched copy owns the spoken turns and
  // the live bubbles step aside. A refetch that never resolves keeps them.
  const [handedOver, setHandedOver] = useState<string | null>(null);
  useEffect(() => {
    if (pip.state !== 'ended') {
      if (pip.state !== 'idle') endedForRef.current = null;
      return;
    }
    if (!sameThread || !pip.conversationId) return;
    if (endedForRef.current === pip.conversationId) return;
    endedForRef.current = pip.conversationId;
    const ended = pip.conversationId;
    const outcome = onCallEndedRef.current?.();
    if (outcome && typeof (outcome as Promise<unknown>).then === 'function') {
      void (outcome as Promise<unknown>).then(
        () => setHandedOver(ended),
        () => {},
      );
    }
  }, [pip.state, pip.conversationId, sameThread]);

  // Same-thread WATERMARK. Voice turns persist per turn DURING the call, so
  // navigating into the live call's own thread fetches every turn so far — and
  // the accumulated live transcript would render all of them a second time.
  // On the transition into the same-thread state, everything already FINAL in
  // the live transcript is (or is about to be) in the fetched history; only
  // segments after the watermark are new. The in-flight interim tail stays
  // live — its turn has not persisted yet.
  const liveStartRef = useRef(0);
  const prevSameThreadRef = useRef(false);
  if (sameThread && !prevSameThreadRef.current) {
    const firstPending = transcript.findIndex((s) => !s.final);
    liveStartRef.current = firstPending === -1 ? transcript.length : firstPending;
  }
  prevSameThreadRef.current = sameThread;

  const voiceMessages = useMemo<Message[]>(() => {
    if (!enabled || pip.state === 'idle') return [];
    if (handedOver && handedOver === pip.conversationId) return [];
    if (sameThread) {
      return transcriptToMessages(transcript.slice(liveStartRef.current), baseTimeRef.current);
    }
    const live = transcriptToMessages(transcript, baseTimeRef.current);
    const prior = priorCaptionsToMessages(
      priorCaptions,
      baseTimeRef.current,
      transcript.map((s) => s.text),
    );
    return [...prior, ...live];
  }, [enabled, pip.state, pip.conversationId, transcript, sameThread, priorCaptions, handedOver]);

  const trySendText = useCallback(
    async (text: string): Promise<'delivered' | 'refused' | false> => {
      if (!isLive) return false;
      try {
        await getPipVoiceSession().sendText(text);
        return 'delivered';
      } catch (error) {
        if (error instanceof DuplexSendError && error.kind === 'not-live') return false;
        setDuplexNotice(chatConfig().copy.voice.duplexUnavailable);
        return 'refused';
      }
    },
    [isLive],
  );

  return {
    enabled,
    pip,
    isLive,
    voiceMessages,
    startVoice,
    trySendText,
    duplexNotice,
    duplexPaused: isLive && pip.duplex === 'unavailable',
  };
}
